import React from 'react';
import ReactDOM from 'react-dom/client';
import './fonts.css';
import './index.css';
import App from './App';
import { ColorModeProvider } from './components/context/ColorModeContext';
import './styles/color-mode.css';
import reportWebVitals from './reportWebVitals';
import axios from 'axios';
import { BRAND } from './generated/brand';
import { getCompanyBrand, loadCompanyBrand } from './utils/company-brand';
import { clearAuthToken, getAuthToken } from './utils/auth-storage';
import { companyFromHost, getCompanyCode } from './utils/company';
import { startDeployVersionWatcher } from './utils/deployVersion';
import { setCompanyFormat } from './utils/format/currency';
import { setOperationalTimezone } from './utils/operationalDateTime';
import { setOverdueBands } from './utils/overdueColors';

const applyBrandLinks = () => {
  const cacheBuster = `client=${encodeURIComponent(BRAND.clientId)}`;

  if (BRAND.web.assetUrls.favicon) {
    document
      .querySelectorAll<HTMLLinkElement>('link[rel="icon"]')
      .forEach(link => {
        link.href = `${BRAND.web.assetUrls.favicon}?${cacheBuster}`;
      });
  }

  if (BRAND.web.assetUrls.appleTouchIcon) {
    document
      .querySelectorAll<HTMLLinkElement>('link[rel="apple-touch-icon"]')
      .forEach(link => {
        link.href = `${BRAND.web.assetUrls.appleTouchIcon}?${cacheBuster}`;
      });
  }
};

axios.defaults.baseURL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  BRAND.web.apiBaseUrl;
axios.defaults.withCredentials = true;

// Marca de la empresa en uso (subdominio o código recordado): colores, nombre y logo del portal.
loadCompanyBrand(companyFromHost() ?? getCompanyCode());

axios.interceptors.request.use(config => {
  const token = getAuthToken();
  const existingAuthorization = (config.headers as Record<string, string> | undefined)?.Authorization;

  if (token && !existingAuthorization) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }

  // Multiempresa: el API sabe a qué empresa va cada petición (el token también la lleva al iniciar sesión).
  const company = getCompanyCode();
  if (company) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)['X-Company'] = company;
  }

  return config;
});

axios.interceptors.response.use(
  response => {
    // Al ingresar y al pedir el usuario actual, el API manda la zona horaria de la empresa.
    const url = String(response.config?.url ?? '').replace(/^\/+/, '');
    if ((url === 'login' || url === 'user') && response.data?.timezone) {
      setOperationalTimezone(response.data.timezone);
    }
    // La moneda y el idioma de la empresa, para los montos.
    if ((url === 'login' || url === 'user') && (response.data?.currency || response.data?.locale)) {
      setCompanyFormat(response.data);
    }
    // Y el semáforo de mora que eligió en el portal.
    if ((url === 'login' || url === 'user') && response.data?.overdueColors) {
      setOverdueBands(response.data.overdueColors);
    }
    return response;
  },
  error => {
    if (error?.response?.status === 401) {
      clearAuthToken();
    }

    return Promise.reject(error);
  }
);

if (document.title === 'Ruttia') {
  document.title = getCompanyBrand()?.name || BRAND.web.appTitle || BRAND.displayName;
}

applyBrandLinks();

startDeployVersionWatcher();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    {/* Tema de Ruttia (claro u oscuro): fuentes de la marca y colores de la empresa en todas las pantallas. */}
    <ColorModeProvider>
      <App />
    </ColorModeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
